import { action, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { SubmissionCommentListState } from '../../../components/organisms/submission_comments/state/SubmissionCommentListState';
import { SubmissionCommentTargetType } from '../../../../commonv3/types/submission_comments';
import {
  ExamPracticalSectionSubmissionToken,
  ExamPracticalSectionSubmissionId,
} from '../../../../commonv3/types/courseExams';
import { ExamPracticalSectionSubmissionShowView } from '../../../types/exam_sections';
import { ExamPracticalSubmissionApis } from '../../../apis/examPracticalSectionSubmissions';
import { SubmitFieldState, SubmissionFieldType } from '../../../../commonv3/types/course_works';
import { FieldStateV3, FormContext } from '../../../../commonv3/states/form/FormStateV3';
import { StringValidators, ArrayValidators } from '../../../../commonv3/states/form/validators/index';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { SubmissionEvaluationResultApis } from '../../../apis/submissionEvaluationResult';
import { translateErrorMessage } from '../../../../mainv2/api/error';
import { SubmissionEvaluationResultState } from '../../../components/organisms/submission_evaluation_results/SubmissionEvaluationResultState';
import { PracticalSubmissionApis } from '../../../../adminv3/api/practical_submissions';

export class ExamPracticalSectionSubmissionShowPageForReviewerState {
  id: ExamPracticalSectionSubmissionId;
  token: ExamPracticalSectionSubmissionToken = null;
  submission: RemoteResourceV3<ExamPracticalSectionSubmissionShowView> = RemoteResourceV3.loading();
  commentState = new SubmissionCommentListState();
  submittedEvaluationBlocks = observable.array();
  evaluationBlockErrors = observable.map();
  reviewFieldStates: SubmitFieldState[] = [];
  loading: boolean;
  submitState = new SubmitState();
  submissionEvaluationResultState = new SubmissionEvaluationResultState();
  confirmedComments: RemoteResourceV3<ExamPracticalSectionSubmissionShowView> = RemoteResourceV3.loading();

  constructor() {
    makeObservable(this, {
      id: observable,
      token: observable,
      submission: observable,
      commentState: observable,
      submittedEvaluationBlocks: observable,
      evaluationBlockErrors: observable,
      reviewFieldStates: observable,
      loading: observable,
      submitState: observable,
      submissionEvaluationResultState: observable,
      confirmedComments: observable,
      init: action.bound,
      validate: action.bound,
      submitComment: action.bound,
      setSubmittedEvaluationBlocks: action.bound,
      submitReview: action.bound,
    });
  }

  async init(token: ExamPracticalSectionSubmissionToken) {
    this.token = token;
    const submission = await this.reloadSubmission();
    if (submission) {
      this.initSubmittedEvaluationBlocks(submission);
      this.initReviewFieldStates(submission);
      await this.commentState.init(SubmissionCommentTargetType.EXAM_PRACTICAL_SECTION_SUBMISSION, submission.id);
      await this.submissionEvaluationResultState.init(submission.token);
    }
    await this.confirmSubmissionUserComments();
  }

  async initSubmittedEvaluationBlocks(submission: ExamPracticalSectionSubmissionShowView) {
    this.submittedEvaluationBlocks.replace(
      submission.section.evaluation_blocks.map((block) => {
        return {
          id: block.id,
          label: block.label,
          items: block.items.map((item) => {
            return {
              ...item,
            };
          }),
        };
      })
    );
  }

  async initReviewFieldStates(submission: ExamPracticalSectionSubmissionShowView) {
    this.reviewFieldStates = submission.section.review_field_definitions.map((definition) => {
      switch (definition.type) {
        case SubmissionFieldType.SINGLE_LINE_TEXT:
        case SubmissionFieldType.MULTI_LINE_TEXT:
          return {
            definition,
            state: new FieldStateV3<string>(definition.required ? [StringValidators.required()] : []),
          };
        case SubmissionFieldType.GOOGLE_URL:
          return {
            definition,
            state: new FieldStateV3<string>(
              definition.required
                ? [StringValidators.required(), StringValidators.googleUrl()]
                : [StringValidators.googleUrl()]
            ),
          };
        case SubmissionFieldType.FILE:
          return {
            definition,
            state: new FieldStateV3<string[]>(
              definition.required ? [ArrayValidators.atLeast(1, 'ファイルをアップロードしてください')] : []
            ),
          };
      }
    });
    this.reviewFieldStates.map((state) =>
      state.state._init(`review-field-${state.definition.uid}`, new FormContext(), null)
    );
  }

  async reloadSubmission() {
    return await updateRemoteResourceV3(
      this.submission,
      (v) => (this.submission = v),
      () => ExamPracticalSubmissionApis.show(this.token)
    );
  }

  async validate() {
    await Promise.all(
      this.reviewFieldStates
        .map((field) => {
          field.state.clearErrors();
          return field.state.validate();
        })
        .concat([this.validateEvaluationBlocks()])
    );
  }

  async validateEvaluationBlocks() {
    this.evaluationBlockErrors.clear();
    this.submittedEvaluationBlocks.forEach((block) =>
      block.items.forEach((item) => {
        if (item.scoreResult == null) this.evaluationBlockErrors.set(item.id, '入力してください');
      })
    );
  }

  hasErrors() {
    return this.reviewFieldStates.some((state) => state.state.hasErrors()) || this.evaluationBlockErrors.size > 0;
  }

  getReviewFieldValues(): SubmitFieldState[] {
    return this.reviewFieldStates.map((field) => {
      return {
        definition: field.definition,
        value: field.state.getDirtyValue(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
    });
  }

  async submitComment() {
    try {
      const comment = await this.commentState.create();
      if (comment) {
        await this.reloadSubmission();
      }
    } catch (e) {}
  }

  setSubmittedEvaluationBlocks(blocks) {
    this.submittedEvaluationBlocks = blocks;
  }

  async submitReview() {
    await this.validate();
    if (this.hasErrors()) {
      return;
    }
    try {
      const review = await this.submitState.operate(() =>
        SubmissionEvaluationResultApis.create(
          this.token,
          this.submittedEvaluationBlocks.toJSON(),
          this.getReviewFieldValues()
        )
      );
      if (review) {
        await this.reloadSubmission();
        await this.submissionEvaluationResultState.init(this.token);
      }
    } catch (e) {
      alert('提出に失敗しました : ' + translateErrorMessage(e));
    }
  }

  async confirmSubmissionUserComments() {
    return await updateRemoteResourceV3(
      this.confirmedComments,
      (v) => (this.confirmedComments = v),
      () => PracticalSubmissionApis.confirmSubmissionUserComments(this.token)
    );
  }
}
